/**
 * Product slice — Redux Toolkit state for storefront product pages.
 *
 * Manages two independent sub-stores:
 *
 *  1. **Detail cache** (SF-PROD-001) — keyed by slug, 5-min TTL.
 *     `fetchProductBySlug` → `bySlug`, `statusBySlug`, `errorBySlug`.
 *
 *  2. **Public list cache** (SF-PROD-002) — keyed by a param-hash,
 *     60s TTL. `fetchPublicProducts` → `listsByKey`, `listStatusByKey`,
 *     `listErrorByKey`, `listFetchedAtByKey`.
 *     Params: `{ taxonomy_path?, sort?, page?, per_page? }`.
 *
 * Architecture: containers dispatch thunks and read selectors; capsules
 * receive plain props.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import * as productsApiModule from '@dfq/shared/api/products';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  ProductDetailDTO,
  PublicProductsListEnvelope,
  PublicProductsListParams,
} from '@dfq/shared/types/product';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Cached result for a single public product list query. */
export interface ProductListEntry {
  products: PublicProductsListEnvelope['products'];
  pagination: PublicProductsListEnvelope['pagination'];
}

export interface ProductState {
  // --- Detail cache (SF-PROD-001) ---
  bySlug: Record<string, ProductDetailDTO>;
  statusBySlug: Record<string, AsyncStatus>;
  errorBySlug: Record<string, string | null>;
  /** HTTP status code from a failed fetch, keyed by slug. */
  httpStatusBySlug: Record<string, number>;
  /** Wall-clock ms of last successful fetch, keyed by slug. */
  fetchedAtBySlug: Record<string, number>;

  // --- Public list cache (SF-PROD-002) ---
  /** Keyed by `paramsToKey(params)` — a stable hash of the query params. */
  listsByKey: Record<string, ProductListEntry>;
  listStatusByKey: Record<string, AsyncStatus>;
  listErrorByKey: Record<string, string | null>;
  listHttpStatusByKey: Record<string, number>;
  /** Wall-clock ms of last successful list fetch, keyed by params hash. */
  listFetchedAtByKey: Record<string, number>;
}

const initialState: ProductState = {
  bySlug: {},
  statusBySlug: {},
  errorBySlug: {},
  httpStatusBySlug: {},
  fetchedAtBySlug: {},
  listsByKey: {},
  listStatusByKey: {},
  listErrorByKey: {},
  listHttpStatusByKey: {},
  listFetchedAtByKey: {},
};

/** Cache TTL in milliseconds (5 minutes) for product detail. */
const PRODUCT_TTL_MS = 5 * 60 * 1000;

/** Cache TTL in milliseconds (60 seconds) for product list pages. */
const PRODUCT_LIST_TTL_MS = 60 * 1000;

/**
 * Strip the internal `force` flag from the dispatch arg, returning only the
 * query params that the API client and cache key care about.
 */
function toQueryParams(
  params: PublicProductsListParams & { force?: boolean },
): PublicProductsListParams {
  const { force: _, ...rest } = params;
  void _;
  return rest;
}

/**
 * Produce a stable string key from the list params so each unique filter +
 * sort + page combination is cached separately.
 */
export function paramsToKey(params: PublicProductsListParams): string {
  const normalized = {
    category_slug: params.category_slug ?? '',
    taxonomy_path: params.taxonomy_path ?? '',
    sort: params.sort ?? 'popular',
    featured: params.featured ?? false,
    page: params.page ?? 1,
    per_page: params.per_page ?? 12,
  };
  return [
    normalized.category_slug,
    normalized.taxonomy_path,
    normalized.sort,
    String(normalized.featured),
    String(normalized.page),
    String(normalized.per_page),
  ].join('|');
}

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

export const fetchProductBySlug = createAsyncThunk<
  ProductDetailDTO,
  { slug: string; force?: boolean },
  {
    rejectValue: { slug: string; message: string; httpStatus: number };
    state: { product: ProductState };
  }
>(
  'product/fetchBySlug',
  async ({ slug }, { rejectWithValue }) => {
    try {
      return await productsApiModule.getProductBySlug(slug);
    } catch (err) {
      const httpStatus = err instanceof ApiError ? err.status : 500;
      const message = err instanceof ApiError ? err.message : 'Failed to load product';
      return rejectWithValue({ slug, message, httpStatus });
    }
  },
  {
    condition: ({ slug, force }, { getState }) => {
      if (force) return true;
      const { product } = getState();
      if (product.statusBySlug[slug] === 'loading') return false;
      const fetchedAt = product.fetchedAtBySlug[slug];
      if (
        product.statusBySlug[slug] === 'succeeded' &&
        fetchedAt !== undefined &&
        Date.now() - fetchedAt < PRODUCT_TTL_MS
      ) {
        return false;
      }
      return true;
    },
  },
);

// ---------------------------------------------------------------------------
// Thunk — public product list (SF-PROD-002)
// ---------------------------------------------------------------------------

export const fetchPublicProducts = createAsyncThunk<
  { key: string; result: PublicProductsListEnvelope },
  PublicProductsListParams & { force?: boolean },
  {
    rejectValue: { key: string; message: string; httpStatus: number };
    state: { product: ProductState };
  }
>(
  'product/fetchPublicProducts',
  async (params, { rejectWithValue }) => {
    const queryParams = toQueryParams(params);
    const key = paramsToKey(queryParams);
    try {
      const result = await productsApiModule.listPublicProducts(queryParams);
      return { key, result };
    } catch (err) {
      const httpStatus = err instanceof ApiError ? err.status : 500;
      const message = err instanceof ApiError ? err.message : 'Failed to load products';
      return rejectWithValue({ key, message, httpStatus });
    }
  },
  {
    condition: (params, { getState }) => {
      if (params.force) return true;
      const { product } = getState();
      const queryParams = toQueryParams(params);
      const key = paramsToKey(queryParams);
      if (product.listStatusByKey[key] === 'loading') return false;
      const fetchedAt = product.listFetchedAtByKey[key];
      if (
        product.listStatusByKey[key] === 'succeeded' &&
        fetchedAt !== undefined &&
        Date.now() - fetchedAt < PRODUCT_LIST_TTL_MS
      ) {
        return false;
      }
      return true;
    },
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    /** Evict a cached product, forcing the next consumer to re-fetch. */
    evictProduct(state, action: { payload: string }) {
      const slug = action.payload;
      delete state.bySlug[slug];
      delete state.statusBySlug[slug];
      delete state.errorBySlug[slug];
      delete state.httpStatusBySlug[slug];
      delete state.fetchedAtBySlug[slug];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductBySlug.pending, (state, action) => {
        const { slug } = action.meta.arg;
        state.statusBySlug[slug] = 'loading';
        state.errorBySlug[slug] = null;
        delete state.httpStatusBySlug[slug];
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        const slug = action.payload.slug;
        state.bySlug[slug] = action.payload;
        state.statusBySlug[slug] = 'succeeded';
        state.errorBySlug[slug] = null;
        state.fetchedAtBySlug[slug] = Date.now();
        delete state.httpStatusBySlug[slug];
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        const slug = action.payload?.slug ?? action.meta.arg.slug;
        state.statusBySlug[slug] = 'failed';
        state.errorBySlug[slug] = action.payload?.message ?? 'Unknown error';
        state.httpStatusBySlug[slug] = action.payload?.httpStatus ?? 500;
      })

      // --- Public list (SF-PROD-002) ---
      .addCase(fetchPublicProducts.pending, (state, action) => {
        const key = paramsToKey(toQueryParams(action.meta.arg));
        state.listStatusByKey[key] = 'loading';
        state.listErrorByKey[key] = null;
        delete state.listHttpStatusByKey[key];
      })
      .addCase(fetchPublicProducts.fulfilled, (state, action) => {
        const { key, result } = action.payload;
        state.listsByKey[key] = result;
        state.listStatusByKey[key] = 'succeeded';
        state.listErrorByKey[key] = null;
        state.listFetchedAtByKey[key] = Date.now();
        delete state.listHttpStatusByKey[key];
      })
      .addCase(fetchPublicProducts.rejected, (state, action) => {
        const key = action.payload?.key ?? paramsToKey(action.meta.arg);
        state.listStatusByKey[key] = 'failed';
        state.listErrorByKey[key] = action.payload?.message ?? 'Unknown error';
        state.listHttpStatusByKey[key] = action.payload?.httpStatus ?? 500;
      });
  },
});

export const { evictProduct } = productSlice.actions;
export const productReducer = productSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const selectProductSlice = (state: { product: ProductState }) => state.product;

export const selectProductBySlug = (slug: string) =>
  createSelector(selectProductSlice, (s) => s.bySlug[slug] ?? null);

export const selectProductStatus = (slug: string) =>
  createSelector(
    selectProductSlice,
    (s) => s.statusBySlug[slug] ?? 'idle',
  );

export const selectProductError = (slug: string) =>
  createSelector(selectProductSlice, (s) => s.errorBySlug[slug] ?? null);

export const selectProductHttpStatus = (slug: string) =>
  createSelector(selectProductSlice, (s) => s.httpStatusBySlug[slug] ?? null);

// ---------------------------------------------------------------------------
// Selectors — public list (SF-PROD-002)
// ---------------------------------------------------------------------------

export const selectProductList = (params: PublicProductsListParams) =>
  createSelector(
    selectProductSlice,
    (s) => s.listsByKey[paramsToKey(params)] ?? null,
  );

export const selectProductListStatus = (params: PublicProductsListParams) =>
  createSelector(
    selectProductSlice,
    (s) => s.listStatusByKey[paramsToKey(params)] ?? 'idle',
  );

export const selectProductListError = (params: PublicProductsListParams) =>
  createSelector(
    selectProductSlice,
    (s) => s.listErrorByKey[paramsToKey(params)] ?? null,
  );

export const selectProductListHttpStatus = (params: PublicProductsListParams) =>
  createSelector(
    selectProductSlice,
    (s) => s.listHttpStatusByKey[paramsToKey(params)] ?? null,
  );

// ---------------------------------------------------------------------------
// Re-export types for slice consumers
// ---------------------------------------------------------------------------

export type { ProductDetailDTO, PublicProductSummaryDTO } from '@dfq/shared/types/product';
